/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.html.dom.create
import kotlinx.html.*
import kotlinx.html.js.*
import org.w3c.dom.*

/**
 * Registration data collected from the registration form
 */
data class RegistrationData(
    val email: String,
    val firstName: String,
    val lastName: String,
    val password: String,
    val postalCode: String,
    val phone: String? = null,
    val dateOfBirth: String? = null
)

/**
 * Password strength levels
 */
enum class PasswordStrength {
    WEAK, FAIR, GOOD, STRONG
}

class RegisterComponent(
    private val isLoading: Boolean,
    private val errorMessage: String?,
    private val onRegister: (RegistrationData) -> Unit,
    private val onBackToLogin: () -> Unit,
    private val onBackToLanding: () -> Unit = {}
) {
    private val inputStyle = """
        width: 100%;
        padding: 14px 16px;
        border: 1px solid #333;
        border-radius: 8px;
        font-size: 16px;
        box-sizing: border-box;
        background-color: #1a1a1e;
        color: #ffffff;
        outline: none;
        transition: border-color 0.2s, box-shadow 0.2s;
    """

    private var formError: String? = null

    fun render(): HTMLElement {
        val element = document.create.div {
            style = """
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                background: linear-gradient(180deg, #0a0a0a 0%, #141418 100%);
                padding: 20px;
            """

            div {
                style = """
                    background: rgba(20, 20, 24, 0.9);
                    border-radius: 16px;
                    padding: 40px 32px;
                    box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                    width: 420px;
                    max-width: 90vw;
                    backdrop-filter: blur(10px);
                """

                // Logo image (clickable to return to landing)
                div {
                    style = """
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        margin-bottom: 8px;
                    """
                    img {
                        src = "/wiz3r_logo.png?v=3"
                        alt = "wiz≡r"
                        style = "height: 70px; cursor: pointer;"
                        onClickFunction = { onBackToLanding() }
                    }
                }

                // Header
                h2 {
                    style = """
                        text-align: center;
                        color: #ffffff;
                        font-size: 24px;
                        font-weight: 600;
                        margin-bottom: 8px;
                        margin-top: 0;
                    """
                    +StringKey.AUTH_CREATE_ACCOUNT.tr()
                }

                p {
                    style = """
                        text-align: center;
                        color: #666;
                        font-size: 14px;
                        margin-bottom: 24px;
                    """
                    +"Start tracking your savings rate today"
                }

                // Registration form
                form {
                    id = "register-form"
                    onSubmitFunction = { event ->
                        event.preventDefault()
                        submitForm()
                    }

                    // Name row (first + last)
                    div {
                        style = "display: flex; gap: 12px; margin-bottom: 16px;"

                        div {
                            style = "flex: 1;"
                            input {
                                type = InputType.text
                                placeholder = "First Name *"
                                id = "firstName"
                                autoFocus = true
                                style = inputStyle
                            }
                        }

                        div {
                            style = "flex: 1;"
                            input {
                                type = InputType.text
                                placeholder = "Last Name *"
                                id = "lastName"
                                style = inputStyle
                            }
                        }
                    }

                    // Email
                    div {
                        style = "margin-bottom: 16px;"
                        input {
                            type = InputType.email
                            placeholder = "Email Address *"
                            id = "email"
                            style = inputStyle
                        }
                    }

                    // Postal Code (mandatory)
                    div {
                        style = "margin-bottom: 16px;"
                        input {
                            type = InputType.text
                            placeholder = "Postal Code *"
                            id = "postalCode"
                            style = inputStyle
                        }
                    }

                    // Phone (optional) with note
                    div {
                        style = "margin-bottom: 16px;"
                        input {
                            type = InputType.tel
                            placeholder = "Phone Number (optional)"
                            id = "phone"
                            style = inputStyle
                        }
                        p {
                            style = "color: #555; font-size: 11px; margin: 6px 0 0 0; line-height: 1.4;"
                            +"Will only be used for text notifications — can opt out at any time"
                        }
                    }

                    // Date of birth (optional)
                    div {
                        style = "margin-bottom: 16px;"
                        label {
                            style = "display: block; color: #666; font-size: 12px; margin-bottom: 6px;"
                            +"Date of Birth (optional)"
                        }
                        input {
                            type = InputType.date
                            id = "dateOfBirth"
                            style = inputStyle + "color-scheme: dark;"
                        }
                    }

                    // Password with strength indicator
                    div {
                        style = "margin-bottom: 8px;"
                        input {
                            type = InputType.password
                            placeholder = "Password *"
                            id = "password"
                            style = inputStyle
                        }
                    }

                    // Password strength indicator
                    div {
                        id = "password-strength-container"
                        style = "margin-bottom: 16px;"

                        // Strength bar
                        div {
                            style = "display: flex; gap: 4px; margin-bottom: 6px;"
                            for (i in 1..4) {
                                div {
                                    id = "strength-bar-$i"
                                    style = """
                                        flex: 1;
                                        height: 4px;
                                        background: #333;
                                        border-radius: 2px;
                                        transition: background-color 0.2s;
                                    """
                                }
                            }
                        }

                        // Requirements list
                        div {
                            style = "font-size: 11px; color: #666; line-height: 1.6;"
                            div {
                                id = "req-length"
                                style = "display: flex; align-items: center; gap: 6px;"
                                span { id = "req-length-icon"; +"○" }
                                span { +StringKey.AUTH_PASSWORD_MIN_LENGTH.tr() }
                            }
                            div {
                                id = "req-uppercase"
                                style = "display: flex; align-items: center; gap: 6px;"
                                span { id = "req-uppercase-icon"; +"○" }
                                span { +StringKey.AUTH_PASSWORD_UPPERCASE.tr() }
                            }
                            div {
                                id = "req-lowercase"
                                style = "display: flex; align-items: center; gap: 6px;"
                                span { id = "req-lowercase-icon"; +"○" }
                                span { +StringKey.AUTH_PASSWORD_LOWERCASE.tr() }
                            }
                            div {
                                id = "req-number"
                                style = "display: flex; align-items: center; gap: 6px;"
                                span { id = "req-number-icon"; +"○" }
                                span { +StringKey.AUTH_PASSWORD_NUMBER.tr() }
                            }
                        }
                    }

                    // Confirm Password
                    div {
                        style = "margin-bottom: 8px;"
                        input {
                            type = InputType.password
                            placeholder = "Confirm Password *"
                            id = "confirmPassword"
                            style = inputStyle
                        }
                    }

                    // Password match indicator
                    div {
                        id = "password-match-indicator"
                        style = "margin-bottom: 16px; font-size: 12px; color: #666; display: none;"
                    }

                    // Error message area
                    div {
                        id = "form-error"
                        style = """
                            color: #ff6b6b;
                            margin-bottom: 16px;
                            font-size: 14px;
                            text-align: center;
                            padding: 10px;
                            background: rgba(255, 107, 107, 0.1);
                            border-radius: 6px;
                            display: none;
                        """
                    }

                    // External error message (from parent)
                    if (errorMessage != null) {
                        div {
                            style = """
                                color: #ff6b6b;
                                margin-bottom: 16px;
                                font-size: 14px;
                                text-align: center;
                                padding: 10px;
                                background: rgba(255, 107, 107, 0.1);
                                border-radius: 6px;
                            """
                            +errorMessage
                        }
                    }

                    // Submit button
                    button {
                        type = ButtonType.submit
                        id = "submit-btn"
                        style = """
                            width: 100%;
                            padding: 14px;
                            background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                            color: #0a0a0a;
                            border: none;
                            border-radius: 8px;
                            font-size: 16px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: transform 0.2s, box-shadow 0.2s;
                            box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3);
                        """
                        if (isLoading) {
                            disabled = true
                            style += "opacity: 0.7; cursor: not-allowed;"
                        }
                        if (isLoading) {
                            +StringKey.AUTH_CREATING_ACCOUNT.tr()
                        } else {
                            +StringKey.AUTH_CREATE_ACCOUNT.tr()
                        }
                    }
                }

                // Back to login link
                div {
                    style = """
                        text-align: center;
                        margin-top: 20px;
                    """
                    span {
                        style = "color: #666; font-size: 14px;"
                        +StringKey.AUTH_ALREADY_HAVE_ACCOUNT.tr()
                        +" "
                    }
                    span {
                        style = """
                            color: #00d9ff;
                            font-size: 14px;
                            cursor: pointer;
                            text-decoration: underline;
                        """
                        onClickFunction = { onBackToLogin() }
                        +StringKey.AUTH_SIGN_IN.tr()
                    }
                }

                // Terms notice
                p {
                    style = """
                        text-align: center;
                        color: #444;
                        font-size: 11px;
                        margin-top: 20px;
                        margin-bottom: 0;
                        line-height: 1.5;
                    """
                    +"By creating an account, you agree to our "
                    span {
                        style = "color: #666; text-decoration: underline; cursor: pointer;"
                        +"Terms of Service"
                    }
                    +" and "
                    span {
                        style = "color: #666; text-decoration: underline; cursor: pointer;"
                        +"Privacy Policy"
                    }
                }
            }
        }

        // Attach password validation listeners after render
        window.setTimeout({
            attachPasswordValidation()
        }, 0)

        return element
    }

    private fun attachPasswordValidation() {
        val passwordInput = document.getElementById("password") as? HTMLInputElement
        val confirmInput = document.getElementById("confirmPassword") as? HTMLInputElement

        passwordInput?.addEventListener("input", {
            updatePasswordStrength(passwordInput.value)
            updatePasswordMatch(passwordInput.value, confirmInput?.value ?: "")
        })

        confirmInput?.addEventListener("input", {
            updatePasswordMatch(passwordInput?.value ?: "", confirmInput.value)
        })
    }

    private fun updatePasswordStrength(password: String) {
        val hasLength = password.length >= 8
        val hasUppercase = password.any { it.isUpperCase() }
        val hasLowercase = password.any { it.isLowerCase() }
        val hasNumber = password.any { it.isDigit() }

        // Update requirement indicators
        updateRequirement("req-length-icon", hasLength)
        updateRequirement("req-uppercase-icon", hasUppercase)
        updateRequirement("req-lowercase-icon", hasLowercase)
        updateRequirement("req-number-icon", hasNumber)

        // Calculate strength
        val score = listOf(hasLength, hasUppercase, hasLowercase, hasNumber).count { it }

        // Update strength bars
        val colors = when (score) {
            0 -> listOf("#333", "#333", "#333", "#333")
            1 -> listOf("#ff4444", "#333", "#333", "#333")
            2 -> listOf("#ffa600", "#ffa600", "#333", "#333")
            3 -> listOf("#ffdd00", "#ffdd00", "#ffdd00", "#333")
            4 -> listOf("#33cc33", "#33cc33", "#33cc33", "#33cc33")
            else -> listOf("#333", "#333", "#333", "#333")
        }

        for (i in 1..4) {
            (document.getElementById("strength-bar-$i") as? HTMLElement)?.style?.backgroundColor = colors[i - 1]
        }
    }

    private fun updateRequirement(iconId: String, met: Boolean) {
        val icon = document.getElementById(iconId) as? HTMLElement
        if (icon != null) {
            icon.textContent = if (met) "✓" else "○"
            icon.style.color = if (met) "#33cc33" else "#666"
        }
    }

    private fun updatePasswordMatch(password: String, confirmPassword: String) {
        val indicator = document.getElementById("password-match-indicator") as? HTMLElement ?: return

        if (confirmPassword.isEmpty()) {
            indicator.style.display = "none"
            return
        }

        indicator.style.display = "block"
        if (password == confirmPassword) {
            indicator.textContent = "✓ Passwords match"
            indicator.style.color = "#33cc33"
        } else {
            indicator.textContent = "✗ Passwords do not match"
            indicator.style.color = "#ff6b6b"
        }
    }

    private fun showError(message: String) {
        val errorDiv = document.getElementById("form-error") as? HTMLElement
        if (errorDiv != null) {
            errorDiv.textContent = message
            errorDiv.style.display = "block"
        }
    }

    private fun hideError() {
        val errorDiv = document.getElementById("form-error") as? HTMLElement
        errorDiv?.style?.display = "none"
    }

    private fun validatePassword(password: String): Boolean {
        return password.length >= 8 &&
                password.any { it.isUpperCase() } &&
                password.any { it.isLowerCase() } &&
                password.any { it.isDigit() }
    }

    private fun submitForm() {
        if (isLoading) return
        hideError()

        val firstName = (document.getElementById("firstName") as? HTMLInputElement)?.value ?: ""
        val lastName = (document.getElementById("lastName") as? HTMLInputElement)?.value ?: ""
        val email = (document.getElementById("email") as? HTMLInputElement)?.value ?: ""
        val postalCode = (document.getElementById("postalCode") as? HTMLInputElement)?.value ?: ""
        val phone = (document.getElementById("phone") as? HTMLInputElement)?.value?.takeIf { it.isNotBlank() }
        val dateOfBirth = (document.getElementById("dateOfBirth") as? HTMLInputElement)?.value?.takeIf { it.isNotBlank() }
        val password = (document.getElementById("password") as? HTMLInputElement)?.value ?: ""
        val confirmPassword = (document.getElementById("confirmPassword") as? HTMLInputElement)?.value ?: ""

        // Validate required fields
        if (firstName.isBlank()) {
            showError("Please enter your first name")
            return
        }
        if (lastName.isBlank()) {
            showError("Please enter your last name")
            return
        }
        if (email.isBlank()) {
            showError("Please enter your email address")
            return
        }
        if (postalCode.isBlank()) {
            showError("Please enter your postal code")
            return
        }
        if (password.isBlank()) {
            showError("Please enter a password")
            return
        }

        // Validate password strength
        if (!validatePassword(password)) {
            showError("Password must be at least 8 characters with uppercase, lowercase, and a number")
            return
        }

        // Validate password match
        if (password != confirmPassword) {
            showError("Passwords do not match")
            return
        }

        onRegister(RegistrationData(
            email = email,
            firstName = firstName,
            lastName = lastName,
            password = password,
            postalCode = postalCode,
            phone = phone,
            dateOfBirth = dateOfBirth
        ))
    }
}
